import {
  AlertCircle, BarChart3, BellRing, Briefcase, Calendar, CalendarDays, CalendarRange, Clock, SlidersHorizontal, Sun,
  LogOut, Repeat, Timer, UserCheck,
  type LucideIcon,
} from '../icons';

/**
 * 근태 메뉴 트리 — «근태» 셸(`AttShell`)의 좌측 칸이 그리는 목록 한 벌.
 *
 * ── 왜 이 파일이 생겼나 (2026-08-29 · 판 A) ──────────────────────────────
 * 근태에는 **집(셸)이 없었다.** `/erp/attendance` · `/codes` · `/team` 이 서로를 모르는
 * 평면 라우트 셋이라, 「근태 관리」라는 묶음을 **담을 그릇 자체가 없었다.** 그래서 관리
 * 화면들은 갈 곳이 없어 「내 근태」 본문 안 카드 묶음(`MyAttendancePage` 의 `ADMIN_LINKS`)이
 * 됐다 — 시안이 없애자던 「출근 찍는 화면 한가운데 회사 설정」이 그대로 남은 것이다.
 * 이 파일과 `AttShell` 이 그 그릇이다.
 *
 * ── 규율 하나: 트리에는 이 셸 안에 사는 것만 ─────────────────────────────
 * **이름만 근태에 두고 누르면 인사 트리가 뜨는 것**이 이 판을 시작하게 만든 병이다.
 * 그래서 트리에는 `/erp/attendance/*` 만 둔다 — 판 A 에서 관리 다섯을 일부러 비워 뒀고,
 * 판 B(2026-08-29)에서 **주소째 옮겨 온 뒤**에 세웠다. `att-menu.test.ts` 가 못 박는다.
 *
 * ── 누가 무엇을 보나 ─────────────────────────────────────────────────────
 * 셸은 하나이고 **트리만 갈라진다**(인사 셸과 같은 규칙) — 직원은 「내 근태」 묶음만,
 * 담당자는 「근태 관리」까지. 2026-08-29 사용자 결정으로 **직원에게도 근태 메뉴를 보여 준다**
 * (시안의 「직원 레일에서 감춘다」는 채택하지 않았다).
 *
 * ⚠ **팀장은 아직 이 트리로 오지 못한다.** 팀장은 역할이 STAFF 라 `isAdmin` 이 거짓이고,
 *   지금은 「내 근태」 머리의 `teamPeek` 링크(403·404 를 물어보고 되면 그린다)로 근태 현황에
 *   간다. **그 링크는 이 판에서 건드리지 않는다** — 없애면 팀장의 길이 사라진다.
 *   트리에 세우는 것은 판 F 의 몫이다(같은 «물어보고 되면» 방식으로).
 */
export interface AttMenuItem {
  to: string;
  label: string;
  /** 항목 아래 한 줄 — "이 화면이 무엇을 정하는가". */
  hint: string;
  icon: LucideIcon;
  /** 셸 밖으로 나가는 항목(트리가 사라진다). 지금은 없다 — 검사가 그것을 못 박는다. */
  leaves?: boolean;
}

export interface AttMenuGroup {
  label: string;
  items: AttMenuItem[];
}

/** 근태 셸의 뿌리 경로. 라우터·레일·트리가 같은 값을 본다. */
export const ATT_HOME_PATH = '/erp/attendance';

/**
 * 전 직원이 보는 묶음.
 * ★ 묶음 이름이 **「내 근태」가 아니다** — 그러면 묶음 라벨과 그 안 첫 항목이 **같은 글자**가 되어
 *   트리에 「내 근태 / 내 근태」가 겹쳐 선다(판 A 구현 중 실측에서 드러났다). 「관리」와 짝이 되는
 *   말은 **「개인」** 이고, 판 C 에서 내 휴가·내 임금이 들어와도 그대로 맞는다.
 */
const MY_GROUP: AttMenuGroup = {
  label: '개인',
  items: [
    { to: ATT_HOME_PATH, label: '내 근태', hint: '출퇴근 · 신청 · 이번 달', icon: Clock },
    /**
     * ★★★ 둘을 **하나로 합쳤다** (2026-09-03 · 정본 P3-3).
     *    「내 휴가 · 출장 · 잔업」 과 「내 임금 · 가산 시간」 은 같은 한 달을 말하면서 다섯 자리가
     *    겹쳤다 — 가산 시간은 서버의 **같은 함수 하나**를 두 화면이 읽고 있었다.
     *    옛 주소 둘은 라우터가 이 주소로 넘긴다(저장해 둔 링크가 살아야 한다).
     */
    /**
     * ★★ 곁말에 **찾을 낱말을 다 적는다.** 합치기 전에는 이름이 「내 휴가 · 출장 · 잔업」 이라
     *    트리 검색으로 「출장」 을 찾을 수 있었다 — 이름을 줄이면 그 길이 **말없이 사라진다.**
     *    합칠 때 잃기 쉬운 것이 이런 것이다(검사가 잡았다).
     */
    { to: '/erp/attendance/me', label: '내 근태 내역', hint: '연차 · 휴가 · 출장 · 잔업 · 가산 시간 · 임금', icon: CalendarDays },
  ],
};

/**
 * 담당자만 보는 묶음.
 * ★ 부제는 **지금 화면이 실제로 하는 일**을 적는다 — 「전사 · 이상 근태」처럼 아직 없는 것을
 *   미리 적으면 이름이 실물보다 앞서고, 그게 이 판을 시작하게 만든 병 중 하나였다.
 */
/**
 * 담당자 묶음에서 **보는 것 둘** — 상수로 뽑아 둔 것은 「조회만 부여받은 사람」의 묶음이
 * **같은 줄을 그대로** 쓰기 위해서다(둘을 따로 적으면 언젠가 한쪽만 고쳐진다).
 */
const COMPANY_STATUS_ITEM: AttMenuItem = {
  to: '/erp/attendance/company', label: '근태 현황', hint: '전사 오늘 근태', icon: BarChart3,
};
const ANOMALIES_ITEM: AttMenuItem = {
  to: '/erp/attendance/anomalies', label: '이상 근태', hint: '미출근 · 퇴근 누락', icon: AlertCircle,
};

const ADMIN_GROUP: AttMenuGroup = {
  label: '근태 관리',
  items: [
    /**
     * ★★★ **자리를 갈랐다** (2026-08-31 · 사용자 결정).
     *    「맡은 조직의 근태현황은 대표·부서장·팀장만 보이면 될 듯하고, 경영·인사 담당부서는
     *     근태관리의 근태 현황으로 보는 게 맞을 듯하다.」
     *
     *    종전에는 **두 묶음이 같은 주소**(`/erp/attendance/team`)를 가리켰다. 그래서 화면이
     *    「나는 어느 자격으로 열렸나」를 몰라 **서버에 찔러 물어야** 했다(전사부터 시도 →
     *    403 이면 팀으로). 팀장이 열 때마다 403 이 셋씩 남던 이유가 그것이다.
     *    **주소가 곧 자격**이 되면 물어볼 일이 없다.
     */
    COMPANY_STATUS_ITEM,
    // 이상 근태 (2026-08-29 · 판 F) — 요약 다섯 칸의 숫자를 **사람으로 펼친 자리**.
    ANOMALIES_ITEM,
    // ★★ 아래 다섯은 2026-08-29 판 B 에서 **주소째** 인사 셸에서 옮겨 왔다.
    //    판 A 때 이 자리를 비워 둔 것은 «이름만 옮기는» 것을 되풀이하지 않기 위해서였다 —
    //    이제 화면이 실제로 이 셸 안에 살므로 트리에 선다.
    { to: '/erp/attendance/hire-dates', label: '입사일 관리', hint: '연차의 유일한 입력', icon: UserCheck },
    { to: '/erp/attendance/leave', label: '연차 관리', hint: '자동 · 조정 · 최종', icon: Calendar },
    // 연차정책 (2026-08-30 · 판 J) — **얼마나 주는지**를 회사가 정한다. 연차 관리 바로 뒤가 맞다:
    //   규칙을 보고 나서 사람별 값을 보는 순서다.
    { to: '/erp/attendance/leave-policy', label: '연차정책', hint: '부여 규칙 · 시행일자별', icon: SlidersHorizontal },
    // 연차촉진 (2026-08-30 · 판 J) — **근기법 §61 법정 절차**. 연차정책 바로 뒤가 맞다:
    //   얼마나 주는지 → 사람별 값 → **다 못 쓰면 언제까지 무엇을 해야 하나** 순서다.
    { to: '/erp/attendance/leave-promotion', label: '연차촉진', hint: '미사용 통보 · 법정 기한', icon: BellRing },
    // 퇴사 정산 (2026-08-30 · 판 J) — 연차의 마지막 장면. 촉진 다음이 맞다.
    { to: '/erp/attendance/leave-settlement', label: '퇴사 정산', hint: '미사용 연차 → 급여', icon: LogOut },
    { to: '/erp/attendance/work-schedules', label: '근무제', hint: '시업 시각이 곧 지각 기준', icon: Briefcase },
    { to: '/erp/attendance/holidays', label: '회사 일정', hint: '공휴일 두 층', icon: Sun },
    { to: '/erp/attendance/wages', label: '연장 정산', hint: '가산 시간 · 예상 수당', icon: Timer },
    /**
     * 휴일대체 (2026-09-06 · 판 B) — 보상휴가 §57. **연장 정산 바로 뒤**가 맞다:
     * 같은 시간을 「수당으로 줄지 휴가로 줄지」이고, 여기서 켜면 저기 금액이 줄어든다.
     * ★ 곁말에 「보상휴가」·「§57」을 다 적는다 — 사람마다 부르는 이름이 다르다.
     */
    { to: '/erp/attendance/substitute', label: '보상휴가', hint: '연장 · 휴일 근무를 휴가로', icon: Repeat },
    { to: '/erp/attendance/codes', label: '기준 설정', hint: '휴가 · 출장 종류', icon: SlidersHorizontal },
  ],
};

/**
 * ★★ **맡은 조직이 있는 사람(팀장·부서장·대표)의 묶음** (2026-08-30 · 사용자 지적).
 *
 *    「우리 조직」 메뉴가 이고 있던 둘 중 **근태 현황은 근태 메뉴의 일**이라 이리로 왔다.
 *    담당자는 「근태 관리」에 이미 있으므로 **겹쳐 붙이지 않는다** — 같은 줄이 두 번 서면
 *    어느 것을 눌러야 하는지 묻게 된다.
 * ★ **판정은 서버가 한다**(`/users/me` 의 `orgScope`) — 팀장은 역할이 STAFF 라
 *   역할로는 가릴 수 없다. 범위(자기 팀인지 전사인지)도 **서버가 정한다**.
 */
const LEAD_GROUP: AttMenuGroup = {
  label: '맡은 조직',
  items: [
    // ★ 여기는 **내 조직**이다 — 전사 요약·이상 근태는 담당자의 자리(근태 관리)로 갔다.
    { to: '/erp/attendance/team', label: '근태 현황', hint: '내 조직의 오늘', icon: BarChart3 },
  ],
};

/**
 * ★★ **교대를 쓰는 회사에만 선다** (2026-08-30 · 판 K).
 *
 * 사용자 지시(2026-08-29): 「교대근무가 없지만, 근무유형에 교대근무가 있는 업체도 있기에
 * 개발하여야 한다. **이 제품은 하나의 회사용이 아니다.**」
 *
 * ★ 그래서 **새 스위치를 만들지 않았다.** 「교대를 씁니까」를 어딘가에서 켜게 하면 켜는 걸
 *   잊은 회사는 화면이 없고, 끈 회사도 표는 그대로 남는다. 대신 **`SHIFT` 근무제가 한 줄이라도
 *   있으면** 이 항목이 선다 — 조를 만든 것이 곧 「우리는 교대를 쓴다」는 뜻이다.
 */
const SHIFT_ITEM: AttMenuItem = {
  to: '/erp/attendance/shifts', label: '교대 편성', hint: '조 · 한 달 표', icon: CalendarRange,
};

/**
 * ★★★ **조회만 부여받은 사람의 묶음** (2026-08-31).
 *
 *    근태 담당자는 역할이 `STAFF` 다(담당자 부여, 근태 C1). 그래서 지금까지 이 사람에게는
 *    **「근태 관리」가 아예 안 섰다** — 부여를 해 줘도 갈 길이 없었다는 뜻이다.
 *    `/users/me` 의 `canSeeCompanyAttendance` 가 그 답을 주면서 이 묶음이 생겼다.
 *
 * ★★ **볼 수 있는 것만 세운다.** 부여된 것은 「남의 근태 조회」(`ATTENDANCE_VIEW`) 하나이므로
 *    **보는 화면 둘**만이다 — 기준 설정 · 근무제 · 연차 부여는 저마다 다른 업무(`WORK_SCHEDULE_MANAGE`
 *    · `LEAVE_GRANT`)이고, 세워 봐야 누르면 403 이다.
 *    **할 수 없는 일을 메뉴에 세우면 그게 잘못된 안내다.**
 * ★ 묶음 이름은 관리자와 **같다** — 같은 자리의 같은 화면이라 다른 이름을 붙이면 둘이 달라 보인다.
 */
const VIEW_GROUP: AttMenuGroup = {
  label: '근태 관리',
  items: [COMPANY_STATUS_ITEM, ANOMALIES_ITEM],
};

export const ATT_MENU: readonly AttMenuGroup[] = [MY_GROUP, ADMIN_GROUP];
export const MY_ATT_MENU: readonly AttMenuGroup[] = [MY_GROUP];

/**
 * 이 사람이 볼 트리.
 *
 * ★ `hasOrg` = **맡은 조직이 있는가**(`orgScope !== 'NONE'`). 담당자가 아니면서 맡은 조직이
 *   있으면 「맡은 조직」 묶음이 붙는다. **담당자에게는 안 붙는다** — 「근태 관리」에 이미 있다.
 * ★ 기본값이 거짓인 것은 **모르면 안 보여 주는 쪽**이 맞기 때문이다(옛 서버는 그 칸을 안 준다).
 */
export function attMenuFor(
  isAdmin: boolean,
  hasOrg = false,
  hasShift = false,
  canSeeCompany = false,
): readonly AttMenuGroup[] {
  if (isAdmin) {
    if (!hasShift) return ATT_MENU;
    // 「근태 현황」 다음, 관리 묶음 안에 선다 — 조를 만든 회사에만.
    return [MY_GROUP, { ...ADMIN_GROUP, items: [...ADMIN_GROUP.items, SHIFT_ITEM] }];
  }
  /**
   * ★★★ **부여받은 사람은 관리자가 아니어도 전사를 본다** (2026-08-31).
   *    「맡은 조직」보다 **먼저** 보는 것은, 전사 현황이 내 조직을 이미 품기 때문이다 —
   *    같은 줄이 두 번 서면 어느 것을 눌러야 하는지 묻게 된다(담당자에게 「맡은 조직」을
   *    안 붙이는 판 F 의 규칙 그대로).
   */
  if (canSeeCompany) return [MY_GROUP, VIEW_GROUP];
  return hasOrg ? [MY_GROUP, LEAD_GROUP] : MY_ATT_MENU;
}

/**
 * 트리 검색 — **목록만 거른다.** 치는 도중에 오른쪽 화면이 발밑에서 바뀌면 안 된다
 * (인사·통합설정 트리와 같은 규칙). 이름과 부제를 함께 본다.
 */
export function filterAttMenu(groups: readonly AttMenuGroup[], query: string): AttMenuGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups.map((g) => ({ ...g, items: [...g.items] }));
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => i.label.toLowerCase().includes(q) || i.hint.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0);
}
